import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Megaphone, Plus, BarChart3, CreditCard, LogOut } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import OtpVerify from "@/components/OtpVerify";
import {
  Inp, StatusPill, CampaignForm, MediaLibrary, AdvertiserAnalyticsBlock,
  CampaignDetailView, AdAnalyticsView, entityAction, CampaignRowActions,
} from "@/pages/admin/AdCMS";

const F = Inp;

function AuthCard({ mode }) {
  const { login } = useApp();
  const nav = useNavigate();
  const [form, setForm] = useState({ company: "", contact: "", email: "", password: "", phone: "", website: "" });
  const [otp, setOtp] = useState(null);
  const verified = (data) => {
    login(data.token, data.user);
    toast.success("Advertiser account created — complete activation to unlock analytics");
    nav("/advertiser");
  };
  const doAuth = async (e) => {
    e.preventDefault();
    try {
      if (mode === "register") {
        const { data } = await api.post("/advertiser/register", form);
        setOtp(data);
        toast.success("Verification code sent to your email");
      } else {
        const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
        if (data.user.role !== "advertiser" && data.user.role !== "admin") return toast.error("Not an advertiser account");
        login(data.token, data.user);
        nav(data.user.role === "admin" ? "/admin" : "/advertiser");
      }
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <div className="min-h-[80vh] grid place-items-center px-4 py-12">
      <div className="w-full max-w-md rounded-3xl border border-border surface-2 p-8 fade-in">
        <Megaphone className="h-10 w-10 text-primary mx-auto" />
        <h1 className="text-2xl font-display font-bold text-center mt-3">{mode === "register" ? "Become an Advertiser" : "Advertiser Login"}</h1>
        {otp ? <OtpVerify email={otp.email} initialCooldown={otp.resend_in} expiresIn={otp.expires_in} onBack={() => setOtp(null)} onVerified={verified} /> : <>
        <form onSubmit={doAuth} className="space-y-3 mt-6">
          {mode === "register" && <>
            <F placeholder="Company / Brand name" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} required data-testid="adv-company" />
            <F placeholder="Contact person" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} required />
            <F placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <F placeholder="Website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
          </>}
          <F type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="adv-email" />
          <F type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="adv-password" />
          {mode !== "register" && <div className="text-right"><Link to="/forgot-password" className="text-xs text-primary" data-testid="adv-forgot-password-link">Forgot password?</Link></div>}
          <button data-testid="adv-auth-submit" className="w-full px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold">{mode === "register" ? "Create Account" : "Sign In"}</button>
        </form>
        <p className="text-center text-sm text-muted-foreground mt-5">
          {mode === "register" ? "Already an advertiser? " : "New advertiser? "}
          <Link to={mode === "register" ? "/advertiser/login" : "/advertiser/register"} className="text-primary">{mode === "register" ? "Sign in" : "Create account"}</Link>
        </p>
        </>}
      </div>
    </div>
  );
}

function ActivationBanner({ user, onPay, paying }) {
  if (user.paid && user.status === "active") return null;
  return (
    <div className="rounded-2xl border border-amber-400/40 bg-amber-400/5 p-5 flex flex-wrap items-center gap-4" data-testid="activation-banner">
      <CreditCard className="h-8 w-8 text-amber-400 shrink-0" />
      <div className="flex-1 min-w-[220px]">
        <p className="font-semibold">Analytics activation required</p>
        <p className="text-sm text-muted-foreground">
          {!user.paid
            ? "Complete the one-time activation payment, then an admin approves your account — analytics unlock after both."
            : "Payment received. Waiting for admin approval to unlock analytics."}
        </p>
      </div>
      {!user.paid && (
        <button onClick={onPay} disabled={paying} data-testid="banner-pay-btn" className="px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50">
          {paying ? "Redirecting…" : "Pay & Activate"}
        </button>
      )}
    </div>
  );
}

function CampaignsHome({ user, onOpenCampaign, lockProps }) {
  const [camps, setCamps] = useState([]);
  const [campForm, setCampForm] = useState(null);
  const load = useCallback(() => {
    api.get(`/adx/advertisers/${user.id}/campaigns`).then((r) => setCamps(r.data)).catch((e) => toast.error(apiError(e)));
  }, [user.id]);
  useEffect(() => { load(); }, [load]);
  const act = async (kind, id, action) => {
    try { if (await entityAction(kind, id, action)) { toast.success("Updated"); load(); } }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-8">
      <section>
        <h2 className="text-xl font-display font-bold mb-4 flex items-center gap-2"><BarChart3 className="h-5 w-5 text-primary" /> Overall Analytics</h2>
        <AdvertiserAnalyticsBlock id={user.id} lockProps={lockProps} />
      </section>
      <section>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="text-xl font-display font-bold">Your Campaigns</h2>
          <button onClick={() => setCampForm("new")} data-testid="create-campaign-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold"><Plus className="h-4 w-4" /> New Campaign</button>
        </div>
        <div className="space-y-2">
          {camps.map((c) => (
            <div key={c.id} className="flex items-center gap-3 p-3 rounded-xl border border-border surface-2 flex-wrap" data-testid={`campaign-row-${c.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">{c.name}</p>
                <p className="text-xs text-muted-foreground">{c.ad_count} ads · {c.impressions} impressions · {c.clicks} clicks</p>
              </div>
              <StatusPill s={c.status} />
              <CampaignRowActions c={c} isAdmin={false} act={act} onEdit={(x) => setCampForm(x)} onAnalytics={onOpenCampaign} onOpen={onOpenCampaign} />
            </div>
          ))}
          {!camps.length && <p className="text-muted-foreground text-center py-10 text-sm">No campaigns yet. Click "New Campaign" to get started.</p>}
        </div>
      </section>
      {campForm && <CampaignForm advertiserId={user.id} campaign={campForm === "new" ? null : campForm} onClose={() => setCampForm(null)} onSaved={() => { setCampForm(null); load(); }} />}
    </div>
  );
}

export default function Advertiser({ mode }) {
  const { user, logout, refreshUser } = useApp();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [view, setView] = useState({ name: "dashboard" });
  const [tab, setTab] = useState("campaigns");
  const [paying, setPaying] = useState(false);
  const [fee, setFee] = useState(29);

  const isAuthMode = mode === "login" || mode === "register";

  useEffect(() => {
    if (isAuthMode) return;
    if (user === undefined) return;
    if (!user || (user.role !== "advertiser" && user.role !== "admin")) { nav("/advertiser/login"); return; }
    api.get("/ads-config").then((r) => setFee(r.data.advertiser_fee || 29)).catch(() => {});
  }, [user, mode]); // eslint-disable-line

  // Stripe redirect-back: poll payment status
  useEffect(() => {
    const sid = sp.get("session_id");
    if (!sid) return;
    let n = 0;
    const t = setInterval(async () => {
      n += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          clearInterval(t);
          await refreshUser();
          toast.success("Payment received! Analytics unlock once an admin approves your account.");
          setSp({}, { replace: true });
        }
      } catch { /* keep polling */ }
      if (n > 12) clearInterval(t);
    }, 1500);
    return () => clearInterval(t);
  }, []); // eslint-disable-line

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/adx/advertiser/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setPaying(false); }
  };

  if (isAuthMode) return <AuthCard mode={mode} />;
  if (!user || user.role === "admin")
    return (
      <div className="min-h-[60vh] grid place-items-center px-4">
        <div className="text-center">
          <Megaphone className="h-10 w-10 text-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">{user?.role === "admin" ? "Admins manage advertising from the Admin CMS." : "Loading…"}</p>
          {user?.role === "admin" && <Link to="/admin" className="inline-block mt-4 px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold">Open Admin CMS</Link>}
        </div>
      </div>
    );

  const lockProps = { onPay: pay, paying, fee };

  return (
    <div className="max-w-[1200px] mx-auto px-4 lg:px-8 py-10 fade-in">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <h1 className="text-4xl font-display font-bold flex items-center gap-3"><Megaphone className="h-8 w-8 text-primary" /> Advertiser Dashboard</h1>
        <div className="flex items-center gap-3">
          <StatusPill s={user.status} />
          <span className={`text-xs font-semibold ${user.paid ? "text-primary" : "text-amber-400"}`} data-testid="adv-paid-badge">{user.paid ? "Paid" : "Unpaid"}</span>
          <button onClick={() => { logout(); nav("/advertiser/login"); }} data-testid="adv-logout" title="Log out" className="p-2 rounded-full border border-border hover:border-primary transition-colors"><LogOut className="h-4 w-4" /></button>
        </div>
      </div>

      {view.name === "dashboard" && (
        <>
          <ActivationBanner user={user} onPay={pay} paying={paying} />
          <div className="flex gap-2 flex-wrap my-6">
            {[["campaigns", "Campaigns & Analytics"], ["library", "Media Library"]].map(([v, l]) => (
              <button key={v} onClick={() => setTab(v)} data-testid={`adv-tab-${v}`}
                className={`px-4 py-2 rounded-full text-sm transition-colors ${tab === v ? "bg-primary text-primary-foreground" : "border border-border hover:border-primary"}`}>{l}</button>
            ))}
          </div>
          {tab === "campaigns"
            ? <CampaignsHome user={user} onOpenCampaign={(c) => setView({ name: "campaign", camp: c })} lockProps={lockProps} />
            : <MediaLibrary />}
        </>
      )}

      {view.name === "campaign" && (
        <CampaignDetailView cid={view.camp.id} isAdmin={false}
          onBack={() => setView({ name: "dashboard" })}
          onOpenAdAnalytics={(a) => setView({ name: "adAnalytics", ad: a, camp: view.camp })} />
      )}

      {view.name === "adAnalytics" && (
        <AdAnalyticsView ad={view.ad} onBack={() => setView(view.camp ? { name: "campaign", camp: view.camp } : { name: "dashboard" })} />
      )}
    </div>
  );
}
